import Badge, { BadgeProps } from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useState } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import AlertTitle from '@mui/material/AlertTitle';
import { ServerStatus } from 'common/models/Common';
import { GetServerStatus } from 'services/CommonService';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    top: 0,
    border: `2px solid #5E21D0; background:red;`,
    padding: '0 4px',
  },
}));

interface Props {
  isSideBarOpen?: boolean;
}

export default function NavBarNotifications({ isSideBarOpen }: Props) {
  const [currentVersion, setCurrentVersion] = useState('');
  const [serverVersion, setServerVersion] = useState('');
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const updateVersions = useCallback((result: ServerStatus | null) => {
    if (result) {
      setCurrentVersion(result.informationalVersion);
      setServerVersion(result.informationalVersion);
    }
  }, []);

  useEffect(() => {
    const getCurrentVersion = async () => {
      const res = await GetServerStatus();
      if (res) {
        updateVersions(res);
      }
    };
    getCurrentVersion();
  }, [updateVersions]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const res = await GetServerStatus();
      if (res) {
        setServerVersion(res.informationalVersion);
        if (currentVersion === serverVersion) {
          setNotificationCount(0);
        } else {
          setNotificationCount(1);
        }
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [currentVersion, serverVersion, notificationCount]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((prev) => (prev ? null : event.currentTarget));
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block', marginTop: '6px' }}>
        <Tooltip
          title="Notifications"
          placement="right"
          disableHoverListener={isSideBarOpen || Boolean(anchorEl)}
        >
          <ListItemButton
            sx={{ paddingY: 0, px: 2.5, justifyContent: isSideBarOpen ? 'initial' : 'center' }}
            onClick={handleClick}
          >
            <ListItemIcon
              sx={{ minWidth: 0, justifyContent: 'center', mr: isSideBarOpen ? 3 : 'auto' }}
            >
              <StyledBadge
                badgeContent={notificationCount}
                style={{ color: 'white', zIndex: '1000' }}
              >
                <NotificationsIcon
                  sx={{ color: (t) => t.palette.getContrastText(t.palette.primary.main) }}
                />
              </StyledBadge>
            </ListItemIcon>
            <ListItemText primary="Notifications" sx={{ opacity: isSideBarOpen ? 1 : 0 }} />
          </ListItemButton>
        </Tooltip>
      </ListItem>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClick={handleClose}
      >
        {currentVersion === serverVersion ? (
          <Typography style={{ color: '#5E21D0', fontWeight: 'bold' }} sx={{ p: 2 }}>
            You have no notifications.
          </Typography>
        ) : (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            }
          >
            <AlertTitle>Recruit Wizard Updated</AlertTitle>
            Your version ({currentVersion}) of Recruit Wizard is out of date, click the refresh
            button to ensure you have the latest version ({serverVersion}).
          </Alert>
        )}
      </Popover>
    </>
  );
}
